import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { useClickOutside } from '../../internals/useClickOutside';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useId } from '../../internals/useId';
import { Typography } from '../../primitives/Typography';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';
import { Search } from '../Search';
import type { SelectOption } from '../Select';

/**
 * Option for SearchPicker. Extends SelectOption with an optional subtitle
 * (e.g. employeeId shown as `#111` under the participant name).
 */
export type SearchPickerOption = SelectOption & {
  subtitle?: string;
  [key: string]: unknown;
};

export interface SearchPickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: SearchPickerOption[];
  value: string | null | undefined;
  onChange: (value: string | null, option: SearchPickerOption | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  clearable?: boolean;
  /**
   * Number of option columns in the panel grid.
   * SearchPicker-only — not related to form-engine matrix `minCols`/`maxCols`.
   * @default 2
   */
  columns?: number;
  /** Prefix prepended to subtitle when rendering (e.g. `#` → `#111`). */
  subtitlePrefix?: string;
  emptyMessage?: string;
  semanticId?: string;
  name?: string;
  /** Panel width hint (legacy CustomDropdown default ~434px). */
  panelWidth?: number | string;
  panelMaxHeight?: number | string;
}

const transientProps = ['$open', '$error', '$disabled', '$placeholder', '$columns'] as const;

const shouldForwardTransient = (prop: string | number): boolean =>
  !transientProps.includes(prop as (typeof transientProps)[number]);

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTrigger = styled.button.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $open: boolean; $error: boolean; $disabled: boolean }>`
  width: 100%;
  height: ${rem(40)};
  min-height: ${rem(40)};
  border-radius: var(--ui-radius-md, ${rem(6)});
  border: ${rem(1)} solid var(--ui-input-border, var(--ui-border, ${palette.LightGray}));
  padding: 0 ${rem(12)};
  background: var(--ui-surface, ${palette.White});
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  filter: drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} var(--ui-input-shadow, var(--ui-shadow-color)));
  transition: border-color 0.2s linear;
  box-sizing: border-box;
  cursor: pointer;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  text-align: left;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: var(--ui-input-border-focus, var(--ui-interactive, ${palette.Blue}));
    outline: none;
  }

  ${({ $error }) =>
    $error &&
    css`
      &:not(:disabled) {
        border-color: var(--ui-status-danger, ${palette.Red});
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      background: var(--ui-background-muted);
      color: var(--ui-text-subtle);
      pointer-events: none;
      opacity: 1;
      filter: none;
    `}

  ${({ $open }) =>
    $open &&
    css`
      border-color: var(--ui-input-border-focus, var(--ui-interactive, ${palette.Blue}));
    `}
`;

const StyledTriggerLabel = styled.span.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${rem(15)};
  line-height: ${rem(16)};
  color: ${({ $placeholder }) =>
    $placeholder
      ? `var(--ui-text-subtle, ${palette.SpanishGray})`
      : `var(--ui-text, ${palette.Black})`};
`;

const StyledTriggerActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(2)};
  flex-shrink: 0;
`;

const StyledIconBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${rem(18)};
  line-height: 1;
  color: var(--ui-text-muted, ${palette.Gray});
  font-variation-settings: 'FILL' 1;
  cursor: pointer;

  &:hover {
    color: var(--ui-text, ${palette.Black});
  }
`;

const StyledChevron = styled.span`
  flex-shrink: 0;
  font-size: ${rem(20)};
  line-height: 1;
  color: var(--ui-text-muted, ${palette.Gray});
  font-variation-settings: 'FILL' 1;
`;

const StyledPanel = styled.div<{ $width?: string; $maxHeight?: string }>`
  position: absolute;
  top: calc(100% + ${rem(8)});
  left: 0;
  z-index: ${zIndex.dropdown};
  width: ${({ $width }) => $width ?? rem(434)};
  min-width: 100%;
  max-height: ${({ $maxHeight }) => $maxHeight ?? rem(275)};
  padding: ${rem(12)} ${rem(14)};
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  box-shadow: var(--ui-shadow-lg, 0 ${rem(2)} ${rem(20)} rgba(22, 22, 22, 0.12));
  border-radius: var(--ui-radius-lg, ${rem(11)});
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
`;

const StyledGrid = styled.div.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  column-gap: ${rem(20)};
  row-gap: ${rem(16)};
  overflow-y: auto;
  flex: 1;
  min-height: ${rem(80)};
  padding-right: ${rem(4)};

  &::-webkit-scrollbar {
    width: ${rem(5)};
  }

  &::-webkit-scrollbar-thumb {
    background: var(--ui-border-strong, ${palette.SpanishGray});
    border-radius: ${rem(7)};
  }
`;

const StyledOption = styled.button.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(2)};
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: inherit;
  color: var(--ui-text, inherit);
  opacity: 1;

  &:hover:not(:disabled) .option-title {
    color: var(--ui-interactive, ${palette.Iris});
  }
`;

const StyledOptionTitle = styled.span`
  font-size: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(18)};
  color: var(--ui-text, ${palette.Black});
  word-break: break-word;
`;

const StyledOptionSubtitle = styled.span`
  font-size: ${rem(12)};
  font-weight: 400;
  line-height: ${rem(16)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
`;

const StyledEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(100)};
  padding: ${rem(16)};
  grid-column: 1 / -1;
`;

const StyledLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(100)};
`;

const loadingDotPulse = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
`;

const StyledLoadingDots = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: center;
  font-size: ${rem(4)};
  line-height: 1;
  color: var(--ui-interactive, ${palette.Iris});
  padding: ${rem(8)};
`;

const StyledDot = styled.span<{ $delay: number; $offset: boolean }>`
  background-color: currentColor;
  border-radius: 1em;
  display: inline-block;
  height: 1em;
  width: 1em;
  margin-left: ${({ $offset }) => ($offset ? '1em' : undefined)};
  animation: ${loadingDotPulse} 1s ease-in-out ${({ $delay }) => $delay}ms infinite;
`;

function LoadingDots({ className }: { className?: string }) {
  return (
    <StyledLoadingDots className={className} aria-hidden>
      <StyledDot $delay={0} $offset={false} />
      <StyledDot $delay={160} $offset />
      <StyledDot $delay={320} $offset />
    </StyledLoadingDots>
  );
}

function toCssSize(value: number | string | undefined, fallback: string): string {
  if (value == null) return fallback;
  return typeof value === 'number' ? rem(value) : value;
}

interface PickerUiState {
  open: boolean;
  search: string;
}

type PickerUiAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE' }
  | { type: 'SET_SEARCH'; query: string }
  | { type: 'CLEAR_SEARCH' };

const initialPickerUiState: PickerUiState = { open: false, search: '' };

function pickerUiReducer(state: PickerUiState, action: PickerUiAction): PickerUiState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, open: true };
    case 'CLOSE':
      return { open: false, search: '' };
    case 'TOGGLE':
      return state.open ? { open: false, search: '' } : { ...state, open: true };
    case 'SET_SEARCH':
      return { ...state, search: action.query };
    case 'CLEAR_SEARCH':
      return { ...state, search: '' };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

export const SearchPicker = React.forwardRef<HTMLDivElement, SearchPickerProps>(
  function SearchPicker(
    {
      options,
      value,
      onChange,
      onBlur,
      placeholder = 'Select',
      searchPlaceholder = 'Search',
      disabled = false,
      loading = false,
      error = false,
      clearable = true,
      columns = 2,
      subtitlePrefix = '',
      emptyMessage = 'No Data Found',
      semanticId,
      name,
      panelWidth = 434,
      panelMaxHeight = 275,
      className,
      style,
      ...rest
    },
    ref,
  ) {
    const [ui, dispatch] = React.useReducer(pickerUiReducer, initialPickerUiState);
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const [panelNarrow, setPanelNarrow] = React.useState(false);
    const listboxId = useId('search-picker-listbox');
    const triggerId = useId('search-picker-trigger');

    React.useEffect(() => {
      const el = panelRef.current;
      if (!el) return;
      const ro = new ResizeObserver(([entry]) => {
        const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        setPanelNarrow(w < 300);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [ui.open]);
    const semanticProps = getSemanticAttributes(semanticId);

    const selected = React.useMemo(
      () => (value ? options.find((o) => o.value === String(value)) ?? null : null),
      [options, value],
    );

    const filtered = React.useMemo(() => {
      const q = ui.search.trim().toLowerCase();
      if (!q) return options;
      return options.filter((o) => {
        const label = String(o.label ?? '').toLowerCase();
        const subtitle = String(o.subtitle ?? '').toLowerCase();
        return label.includes(q) || subtitle.includes(q);
      });
    }, [options, ui.search]);

    const close = React.useCallback(() => {
      dispatch({ type: 'CLOSE' });
    }, []);

    useClickOutside(rootRef, close, ui.open);
    useEscapeKey(() => {
      close();
      triggerRef.current?.focus();
    }, ui.open);

    const toggleOpen = React.useCallback(() => {
      if (disabled) return;
      dispatch({ type: 'TOGGLE' });
    }, [disabled]);

    const handleSelect = React.useCallback(
      (option: SearchPickerOption) => {
        if (disabled || option.isDisabled) return;
        onChange(option.value, option);
        close();
      },
      [close, disabled, onChange],
    );

    const handleClear = React.useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        if (disabled) return;
        onChange(null, null);
      },
      [disabled, onChange],
    );

    const composedRootRef = React.useMemo(() => composeRefs(ref, rootRef), [ref]);
    const selectedLabel = selected?.label ?? null;
    const showClear = clearable && Boolean(value) && !disabled;

    return (
      <StyledRoot
        ref={composedRootRef}
        className={className}
        style={style}
        data-ui-search-picker=""
        data-state={ui.open ? 'open' : 'closed'}
        data-disabled={disabled || undefined}
        {...semanticProps}
        {...rest}
      >
        <StyledTrigger
          ref={triggerRef}
          type="button"
          id={semanticId ?? triggerId}
          name={name}
          $open={ui.open}
          $error={error}
          $disabled={disabled}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={ui.open}
          aria-controls={ui.open ? listboxId : undefined}
          aria-invalid={error || undefined}
          onClick={toggleOpen}
          onBlur={onBlur}
        >
          <StyledTriggerLabel $placeholder={!selectedLabel}>
            {selectedLabel ?? placeholder}
          </StyledTriggerLabel>
          <StyledTriggerActions>
            {showClear ? (
              <StyledIconBtn
                className="material-symbols-rounded"
                role="button"
                aria-label="Clear selection"
                onClick={handleClear}
              >
                close
              </StyledIconBtn>
            ) : null}
            {loading && !ui.open ? (
              <LoadingDots />
            ) : (
              <StyledChevron className="material-symbols-rounded" aria-hidden>
                arrow_drop_down
              </StyledChevron>
            )}
          </StyledTriggerActions>
        </StyledTrigger>

        {ui.open && (
          <StyledPanel
            ref={panelRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={semanticId ?? triggerId}
            $width={toCssSize(panelWidth, rem(434))}
            $maxHeight={toCssSize(panelMaxHeight, rem(275))}
          >
            <Search
              semanticId={`${semanticId ?? name ?? 'searchPicker'}.search`}
              placeholder={searchPlaceholder}
              value={ui.search}
              onValueChange={(query) => dispatch({ type: 'SET_SEARCH', query })}
              onClear={() => dispatch({ type: 'CLEAR_SEARCH' })}
            />
            {loading ? (
              <StyledLoading>
                <LoadingDots />
              </StyledLoading>
            ) : (
              <StyledGrid $columns={panelNarrow ? 1 : Math.max(1, columns)}>
                {filtered.length === 0 ? (
                  <StyledEmpty>
                    <Typography variant="body-s" color="var(--ui-text-muted)">
                      {emptyMessage}
                    </Typography>
                  </StyledEmpty>
                ) : (
                  filtered.map((option) => {
                    const optionDisabled = Boolean(option.isDisabled);
                    const subtitle =
                      option.subtitle != null && String(option.subtitle).length > 0
                        ? `${subtitlePrefix}${option.subtitle}`
                        : null;
                    return (
                      <StyledOption
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={option.value === String(value ?? '')}
                        $disabled={optionDisabled}
                        disabled={disabled || optionDisabled}
                        onClick={() => handleSelect(option)}
                      >
                        <StyledOptionTitle className="option-title">{option.label}</StyledOptionTitle>
                        {subtitle ? (
                          <StyledOptionSubtitle>{subtitle}</StyledOptionSubtitle>
                        ) : null}
                      </StyledOption>
                    );
                  })
                )}
              </StyledGrid>
            )}
          </StyledPanel>
        )}
      </StyledRoot>
    );
  },
);

SearchPicker.displayName = 'SearchPicker';
